import React, { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-fullscreen';

// Fix for default icon issue with webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function InteractiveMap({ geoJsonData, onDrawChange }) {
  const mapRef = useRef(null);
  const geoJsonLayerRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) {
      const map = L.map('map', {
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: 'topright',
          title: 'View Fullscreen',
          titleCancel: 'Exit Fullscreen',
        },
      }).setView([51.505, -0.09], 13);

      // Base layers
      const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      });

      const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '&copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
      });

      const topoLayer = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors'
      });

      // Add default layer
      osmLayer.addTo(map);

      // Layer control
      const baseLayers = {
        'OpenStreetMap': osmLayer,
        'Satellite': satelliteLayer,
        'Topographic': topoLayer,
      };
      L.control.layers(baseLayers, null, { position: 'topright' }).addTo(map);

      // Scale control
      L.control.scale({ position: 'bottomleft', imperial: false }).addTo(map);

      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);

      const drawControl = new L.Control.Draw({
        position: 'topleft',
        edit: { featureGroup: drawnItems },
        draw: {
          polygon: true,
          polyline: true,
          rectangle: true,
          circle: true,
          marker: true,
          circlemarker: false,
        },
      });
      map.addControl(drawControl);

      map.on(L.Draw.Event.CREATED, (event) => {
        const layer = event.layer;
        drawnItems.addLayer(layer);
        const newGeoJson = drawnItems.toGeoJSON();
        onDrawChange(newGeoJson);
      });

      map.on('draw:edited', () => {
        onDrawChange(drawnItems.toGeoJSON());
      });

      map.on('draw:deleted', () => {
        onDrawChange(drawnItems.toGeoJSON());
      });

      mapRef.current = map;
      geoJsonLayerRef.current = drawnItems;
    }
  }, [onDrawChange]);

  // Update map with GeoJSON data
  useEffect(() => {
    const layer = geoJsonLayerRef.current;
    if (layer) {
      layer.clearLayers();
      if (geoJsonData && geoJsonData.features && geoJsonData.features.length > 0) {
        const newLayer = L.geoJSON(geoJsonData);
        layer.addLayer(newLayer);

        // Fit the map to the bounds of the new data
        const bounds = newLayer.getBounds();
        if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    }
  }, [geoJsonData]);

  return <div id="map" style={{ height: '100%', width: '100%' }} />;
}

export default InteractiveMap;